import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
  fetchApiPostBlob,
  MimeType,
} from "../utils";
import { BaseDbObject } from "@/lib/models/db";
import { DbUser } from "@/lib/models";
import { CreateContractTemplateValidation, DocumentTemplateInstance, DocumentTemplate } from "../signingContracts";
import { DocumentSigningStatusOptions, DocumentSigningStatus, getSigningStatusLabel } from "../signingContracts/index";
import { parsePhoneNumberToE164Strict } from "@/lib/validation/phoneNumber";
export { DocumentSigningStatusOptions, DocumentSigningStatus, getSigningStatusLabel };


export enum AcceptedAchFields {
  // System fields
  CUSTOMER_NAME = "customer_name",
  CUSTOMER_PHONE = "customer_phone",
  CUSTOMER_EMAIL = "customer_email",

  // Signature fields (plain text)
  CUSTOMER_INSTITUTION_NAME = "customer_institution_name",
  CUSTOMER_ACCOUNT_CURRENCY = "customer_account_currency",
  CUSTOMER_BANK_NUMBER = "customer_bank_number",
  CUSTOMER_BRANCH_NUMBER = "customer_branch_number",
  CUSTOMER_ROUTING_NUMBER = "customer_routing_number",
  CUSTOMER_ACCOUNT_NUMBER = "customer_account_number",

  // Signature fields (date_signed)
  CUSTOMER_DATE = "customer_date",
  LENDER_DATE = "lender_date",

  // Signature fields (other)
  CUSTOMER_SIGNATURE = "customer_signature",
  LENDER_SIGNATURE = "lender_signature",
  LENDER_PRINTED_NAME = "lender_printed_name",
  LENDER_INSTITUTION = "lender_institution",
  LENDER_TITLE = "lender_title",
  LENDER_EMAIL = "lender_email",

  VOIDED_CHECK_ATTACHMENT = "attachment_voided_check",
}

export interface AchDocument extends BaseDbObject {
  AchInstances?: AchDocumentInstance[];
  DocumentTemplate?: DocumentTemplate;
}

export interface AchDocumentInstance extends BaseDbObject {
  customer_name: string;
  customer_email: string;

  lender_name?: string;
  lender_institution?: string;
  lender_title?: string;
  lender_email?: string;

  group_id: string;


  ach_document_id: string | null;
  document_template_instance_id: string;

  AchDocument?: AchDocument;
  DocumentTemplateInstance?: DocumentTemplateInstance;
}

export type DocumentReviewInstanceStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "cancelled";

export type DocumentReviewStageInstanceStatus =
  | "pending"
  | "in_progress"
  | "approved"
  | "denied"
  | "skipped";

export type DocumentReviewStageDecisionStatus = "approved" | "denied" | "skipped";

export type DocumentReviewStageUser = Pick<DbUser, "id" | "name" | "email">;

export interface DocumentReviewWorkflowStage extends BaseDbObject {
  workflow_id: string;
  stage_order: number;
  assigned_user_id: string;
  name: string | null;
  deleted_at: Date | null;
  AssignedUser?: DocumentReviewStageUser | null;
}

export interface DocumentReviewWorkflow extends BaseDbObject {
  feature_type: "ach";
  review_type: string;
  is_active: boolean;
  deleted_at: Date | null;
  Stages: DocumentReviewWorkflowStage[];
}

interface DocumentReviewStageInstanceBase extends BaseDbObject {
  review_instance_id: string;
  review_stage_id: string;
  assigned_user_id: string;
  status: DocumentReviewStageInstanceStatus;
  started_at: Date | null;
  completed_at: Date | null;
  reviewed_by_user_id: string | null;
  reason: string | null;
  notes: string | null;
}

export interface DocumentReviewStageInstanceSummary extends DocumentReviewStageInstanceBase {
  ReviewStage: DocumentReviewWorkflowStage;
  AssignedUser?: DocumentReviewStageUser | null;
  ReviewedBy?: DocumentReviewStageUser | null;
}

export interface DocumentReviewInstanceSummary extends BaseDbObject {
  workflow_id: string;
  feature_type: "ach";
  ach_document_instance_id: string | null;
  task_instance_id: string | null;
  status: DocumentReviewInstanceStatus;
  started_at: Date | null;
  completed_at: Date | null;
  cancelled_at: Date | null;
  Workflow: DocumentReviewWorkflow;
  AchDocumentInstance: AchDocumentInstance | null;
  StageInstances: DocumentReviewStageInstanceSummary[];
}

export interface DocumentReviewStageInstanceDetailed extends DocumentReviewStageInstanceBase {
  ReviewInstance: DocumentReviewInstanceSummary;
  ReviewStage: DocumentReviewWorkflowStage;
  AssignedUser?: DocumentReviewStageUser | null;
  ReviewedBy?: DocumentReviewStageUser | null;
}

export interface ListAchReviewInstancesResponse {
  instances: DocumentReviewInstanceSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface ListAchReviewInstancesParams {
  status?: DocumentReviewInstanceStatus;
  task_instance_id?: string;
  workflow_id?: string;
  page?: number | string;
  limit?: number | string;
}

export interface ApproveAchReviewStageInstancePayload {
  stage_instance_id: string;
  status: DocumentReviewStageDecisionStatus;
  reason?: string;
  notes?: string;
}

export interface ApproveAchReviewStageInstanceResponse {
  stage_instance: DocumentReviewStageInstanceDetailed;
}

export type ListAchTemplateItem = Pick<AchDocument, "id" | "created_at" | "DocumentTemplate">;

export interface AchListTemplateResponse {
  data: ListAchTemplateItem[]
}

const accepted_fields = Object.values(AcceptedAchFields);

export const acceptedFieldsOptions = [
  { value: AcceptedAchFields.CUSTOMER_NAME, label: "Customer Name" },
  { value: AcceptedAchFields.CUSTOMER_EMAIL, label: "Customer Email" },
  { value: AcceptedAchFields.CUSTOMER_PHONE, label: "Customer Phone" },
  { value: AcceptedAchFields.CUSTOMER_INSTITUTION_NAME, label: "Institution Name" },
  { value: AcceptedAchFields.CUSTOMER_BANK_NUMBER, label: "Bank Number" },
  { value: AcceptedAchFields.CUSTOMER_BRANCH_NUMBER, label: "Branch Number" },
  { value: AcceptedAchFields.CUSTOMER_ACCOUNT_CURRENCY, label: "Account Currency" },
  { value: AcceptedAchFields.CUSTOMER_ROUTING_NUMBER, label: "Routing Number" },
  { value: AcceptedAchFields.CUSTOMER_ACCOUNT_NUMBER, label: "Account Number" },
  { value: AcceptedAchFields.CUSTOMER_DATE, label: "Customer Date" },
  { value: AcceptedAchFields.LENDER_DATE, label: "Lender Date" },
  { value: AcceptedAchFields.CUSTOMER_SIGNATURE, label: "Customer Signature" },
  { value: AcceptedAchFields.LENDER_SIGNATURE, label: "Lender Signature" },
  { value: AcceptedAchFields.LENDER_PRINTED_NAME, label: "Lender Printed Name" },
  { value: AcceptedAchFields.LENDER_INSTITUTION, label: "Lender Institution" },
  { value: AcceptedAchFields.LENDER_TITLE, label: "Lender Title" },
  { value: AcceptedAchFields.LENDER_EMAIL, label: "Lender Email" },
  { value: AcceptedAchFields.VOIDED_CHECK_ATTACHMENT, label: "Voided Check" },
];

export const AchOptionalFieldInfo = z.object({
});

export const CreateTemplateInstanceValidation = z.object({
  ach_document_id: z.string().uuid(),
  group_id: z.string().uuid(),
  template_id: z.string().uuid(),
  document_title: z.string().optional(),
  is_test_send: z.boolean().default(false),
  is_draft: z.boolean().default(false),
  meta_fields: AchOptionalFieldInfo.optional(),
  suggestion_id: z.string().uuid().optional(),
  task_instance_id: z.string().uuid().optional(),
  customer_name: z.string(),
  customer_email: z.string().email(),
  customer_phone_number: z.string().optional().transform(parsePhoneNumberToE164Strict),
  lender_email: z.string().email().optional(),
  lender_name: z.string().optional(),
  lender_institution: z.string().optional(),
  lender_phone_number: z.string().optional().transform(parsePhoneNumberToE164Strict),
  lender_title: z.string().optional(),

  has_lienholder: z.boolean(),
}).refine((data: any) => {
  if (data.has_lienholder) {
    return data.lender_email?.trim() && data.lender_institution?.trim();
  }
  return true;
}, {
  message: "Lender must have an email and institution",
  path: ["lender_email", "lender_institution"]
});

export const CreateTemplateValidation = z.object({
  ach_variant: z.enum(["with_lien_holder", "without_lien_holder"]),

  template_content_type: z.enum(["ach"]),
  parent_group_id: z.string().uuid().optional(),

  template: CreateContractTemplateValidation
}).refine((data) => {
  const fields = data.template.fields || [];
  const field_ids: string[] = fields.map((field) => field.field_id);

  const uniqueFieldIds = new Set(field_ids);
  return field_ids.length === uniqueFieldIds.size && field_ids.every((id: string) => accepted_fields.includes(id as AcceptedAchFields));
}, {
  message: "May not duplicate fields or use unknown fields",
  path: ["template", "fields"]
});

export function useCreateAchTemplate() {
  const queryClient = useQueryClient();

  return async (params: typeof CreateTemplateValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ upload_url: string; cfr_document_id: string; }>(
      `contracts/ach/template`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["ach"] });
    }

    return result;
  };
}

export function useCreateAchTemplateInstance() {
  const queryClient = useQueryClient();

  return async (params: typeof CreateTemplateInstanceValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{}>(
      `contracts/ach/template/instance`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["ach"] });
    }

    return result;
  };
}

export function useListAchTemplates() {
  return useQuery({
    queryKey: ["ach", "templates"],
    queryFn: () => fetchApiGet<AchListTemplateResponse>(`contracts/ach/template`),
    retry: 1,
  });
}

export function useGetAchTemplate({ id, enabled }: { id: string; enabled?: boolean }) {
  return useQuery({
    queryKey: ["ach", "template", id],
    queryFn: () => fetchApiGet<AchDocument>(`contracts/ach/template/${id}`),
    retry: 1,
    enabled
  });
}

export function useGetAchTemplateDocument() {
  const queryClient = useQueryClient();

  return async (template_id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ document_url: string; }>(
      `contracts/ach/template/${template_id}/file`,
      {},
      queryParams
    );

    return result;
  };
}

export function useGetAchInstanceDocument() {
  const queryClient = useQueryClient();

  return async (template_id: string, instance_id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ sent_document_url?: string; final_document_url: string; }>(
      `contracts/ach/template/${template_id}/instance/${instance_id}/file`,
      {},
      queryParams
    );

    return result;
  };
}

export function useGetAchInstance({ instance_id, template_id, enabled }: { template_id: string; instance_id: string; enabled?: boolean }) {
  return useQuery({
    queryKey: ["ach", "template", template_id, "instance", instance_id],
    queryFn: () => fetchApiGet<AchDocumentInstance>(`contracts/ach/template/${template_id}/instance/${instance_id}`),
    retry: 1,
    enabled
  });
}

const DirectValuesSchema = z.object({
  customer_name: z.string().optional(),
  customer_email: z.string().email().optional(),
  customer_phone_number: z.string().optional().transform(parsePhoneNumberToE164Strict),
  lender_email: z.string().email().optional(),
  lender_name: z.string().optional(),
  lender_institution: z.string().optional(),
  lender_phone_number: z.string().optional().transform(parsePhoneNumberToE164Strict),
  lender_title: z.string().optional(),
  meta_fields: AchOptionalFieldInfo.optional(),
  has_lienholder: z.boolean(),
});

export const PreviewBodyValidation = z.object({
    task_instance_id: z.string().uuid().optional(),
})
.merge(DirectValuesSchema)
.refine((data: any) => {
  if (data.has_lienholder) {
    return data.lender_email?.trim() && data.lender_institution?.trim();
  }
  return true;
}, {
  message: "Lender must have an email and institution",
  path: ["lender_email", "lender_institution"]
})
.refine(
    (data) => data.task_instance_id || (data.customer_name && data.customer_email),
    {
      message: "Either task_instance_id or both customer_name and customer_email must be provided",
    }
);

export const AchPreviewRequestSchema = z.object({
  params: z.object({
    template_id: z.string().uuid(),
  }),
  body: PreviewBodyValidation,
});

export function useGetAchPreviewTemplateDocument() {
  const queryClient = useQueryClient();

  return async (params: {
    template_id: string;
    task_instance_id?: string;
    customer_name?: string;
    customer_email?: string;
    customer_phone_number?: string;
    lender_email?: string;
    lender_name?: string;
    lender_institution?: string;
    lender_phone_number?: string;
    lender_title?: string;
    meta_fields?: typeof AchOptionalFieldInfo._type;
  }) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPostBlob(
      `contracts/ach/template/${params.template_id}/preview`,
      {
        task_instance_id: params.task_instance_id,
        customer_name: params.customer_name,
        customer_email: params.customer_email,
        customer_phone_number: params.customer_phone_number,
        lender_email: params.lender_email,
        lender_name: params.lender_name,
        lender_institution: params.lender_institution,
        lender_phone_number: params.lender_phone_number,
        lender_title: params.lender_title,
        meta_fields: params.meta_fields,
      },
      MimeType.Json,
      queryParams
    );

    return result;
  };
}

export function listAchReviewInstances(params?: ListAchReviewInstancesParams) {
  return fetchApiGet<ListAchReviewInstancesResponse>(
    `contracts/ach/review-instances`,
    buildAchReviewInstancesQueryParams(params)
  );
}

export interface UseListAchReviewInstancesOptions {
  enabled?: boolean;
}

export function useListAchReviewInstances(
  params?: ListAchReviewInstancesParams,
  options?: UseListAchReviewInstancesOptions
) {
  const serializedParams = buildAchReviewInstancesQueryParams(params).toString();

  return useQuery({
    queryKey: ["ach", "review-instances", serializedParams],
    queryFn: () => listAchReviewInstances(params),
    enabled: options?.enabled,
    retry: 1,
  });
}

export function approveAchReviewStageInstance(payload: ApproveAchReviewStageInstancePayload) {
  return fetchApiPost<ApproveAchReviewStageInstanceResponse>(
    `contracts/ach/review-instances/approve`,
    payload
  );
}

export function useApproveAchReviewStageInstance() {
  const queryClient = useQueryClient();

  return async (payload: ApproveAchReviewStageInstancePayload) => {
    const result = await approveAchReviewStageInstance(payload);

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["ach", "review-instances"] });
    }

    return result;
  };
}

function buildAchReviewInstancesQueryParams(params?: ListAchReviewInstancesParams) {
  const queryParams = new URLSearchParams();

  if (!params) {
    return queryParams;
  }

  if (params.status) {
    queryParams.set("status", params.status);
  }

  if (params.task_instance_id) {
    queryParams.set("task_instance_id", params.task_instance_id);
  }

  if (params.workflow_id) {
    queryParams.set("workflow_id", params.workflow_id);
  }

  if (params.page !== undefined && params.page !== null) {
    queryParams.set("page", params.page.toString());
  }

  if (params.limit !== undefined && params.limit !== null) {
    queryParams.set("limit", params.limit.toString());
  }

  return queryParams;
}
